/**
 * Intake controller calls must use a dedicated axios instance with only the temporary
 * JWT from ValidateKey. Do not route these through ApiService.getClient() — that attaches
 * the portal session token and breaks intake authorization.
 */
import axios from "axios";
import { getApiBaseUrl } from "@/services/ApiService";
import type {
  ApiBoolResult,
  InsertPatientPortalUserBody,
  IntakeCredentials,
  IntakeUser,
  UpdatePatientPortalUserPwdBody,
} from "@/types/intake";

/**
 * Anonymous POST — do not attach portal JWT. Matches [AllowAnonymous] on Portal/ValidateKey.
 */
export async function validateIntakeKey(
  credentials: IntakeCredentials
): Promise<IntakeUser> {
  const client = axios.create({
    baseURL: getApiBaseUrl(),
    headers: { "Content-Type": "application/json" },
  });
  const res = await client.post<IntakeUser>("/Portal/ValidateKey", credentials);
  return res.data;
}

/**
 * Intake controller only — Authorization must be the temporary JWT from ValidateKey.
 * Do not use getClient(); never merge with the portal session token.
 */
function createIntakeBearerClient(intakeToken: string) {
  return axios.create({
    baseURL: getApiBaseUrl(),
    headers: {
      "Content-Type": "application/json",
      Authorization: `bearer ${intakeToken}`,
    },
  });
}

export async function intakeUpdateUserPwd(
  body: UpdatePatientPortalUserPwdBody,
  intakeToken: string
): Promise<ApiBoolResult> {
  const client = createIntakeBearerClient(intakeToken);
  const res = await client.post<ApiBoolResult>("/Intake/UpdateUserPwd", body);
  return res.data;
}

export async function intakeInsertUser(
  body: InsertPatientPortalUserBody,
  intakeToken: string
): Promise<ApiBoolResult> {
  const client = createIntakeBearerClient(intakeToken);
  const res = await client.post<ApiBoolResult>("/Intake/InsertUser", body);
  return res.data;
}
